import type { Pose } from '@sw/schema'
import type { SessionEventView } from '../api/client.js'

export interface SocketHandlers {
  onEvent?: (event: SessionEventView) => void
  onActions?: (actions: { ruleId: string; action: { type: string; [k: string]: unknown }; atMs: number }[]) => void
  onPresence?: (presence: {
    participantId: string
    name: string
    pose: Pose
    health: Record<string, unknown> | null
  }) => void
  onStatus?: (status: 'connecting' | 'open' | 'closed') => void
  /** Who is speaking in this world, or null when nobody is. */
  onBroadcaster?: (broadcaster: { participantId: string; name: string; position: { x: number; y: number } | null } | null) => void
  /** A peer-connection message addressed to us. */
  onRtc?: (from: string, payload: unknown) => void
  onBroadcastRefused?: (message: string) => void
  /** The server rejected the connection; no further attempts will be made. */
  onRefused?: (message: string) => void
}

/**
 * The session socket, with the reconnection behaviour a phone in a pocket
 * actually needs.
 *
 * Two things matter more than they look. The last sequence number seen is
 * remembered, so coming back after a dropout asks only for the gap rather than
 * replaying an hour. And every pong carries the server's clock, which is what
 * lets a session-wide cue be scheduled to land at the same instant on every
 * device rather than whenever each one happens to notice.
 */
export class SessionSocket {
  private socket: WebSocket | undefined
  private closedByUs = false
  private reconnectDelayMs = 500
  private pingTimer: number | undefined
  private reconnectTimer: number | undefined
  private lastSeq = 0
  private offsetMs = 0

  /**
   * The token is read fresh on every connect rather than captured once: an
   * access token lasts fifteen minutes, and a socket that replays an expired
   * one on reconnect is refused every time, forever, with nothing to show for
   * it but a "solo mode" pill.
   */
  constructor(
    private readonly sessionId: string,
    private readonly participantId: string,
    private readonly readToken: () => string | null,
    private readonly handlers: SocketHandlers = {},
  ) {}

  /** Server time, as best this client can tell. */
  get serverNow(): number {
    return Date.now() + this.offsetMs
  }

  get clockOffsetMs(): number {
    return this.offsetMs
  }

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN
  }

  connect(): void {
    this.closedByUs = false
    window.clearTimeout(this.reconnectTimer)
    this.reconnectTimer = undefined
    this.handlers.onStatus?.('connecting')

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const socket = new WebSocket(`${protocol}//${location.host}/v1/session-socket`)
    this.socket = socket

    socket.addEventListener('open', () => {
      this.reconnectDelayMs = 500
      socket.send(
        JSON.stringify({
          type: 'hello',
          token: this.readToken() ?? '',
          sessionId: this.sessionId,
          participantId: this.participantId,
          resumeSeq: this.lastSeq,
        }),
      )
      this.handlers.onStatus?.('open')
      this.pingTimer = window.setInterval(() => this.ping(), 5000)
      this.ping()
    })

    socket.addEventListener('message', (message) => {
      const payload = JSON.parse(String(message.data)) as Record<string, any>
      switch (payload.type) {
        case 'welcome':
          if (payload.broadcaster !== undefined) {
            this.handlers.onBroadcaster?.(payload.broadcaster ?? null)
          }
          break
        case 'event':
          this.lastSeq = Math.max(this.lastSeq, payload.event.seq ?? 0)
          this.handlers.onEvent?.(payload.event)
          break
        case 'actions':
          this.handlers.onActions?.(payload.actions)
          break
        case 'presence':
          this.handlers.onPresence?.({
            participantId: payload.participantId,
            name: payload.name,
            pose: payload.pose,
            health: payload.health ?? null,
          })
          break
        case 'pong': {
          const roundTrip = Date.now() - payload.t
          this.offsetMs = payload.serverTime - (payload.t + roundTrip / 2)
          break
        }
        case 'broadcaster':
          this.handlers.onBroadcaster?.(payload.broadcaster ?? null)
          break
        case 'rtc':
          this.handlers.onRtc?.(String(payload.from), payload.payload)
          break
        case 'broadcastRefused':
          this.handlers.onBroadcastRefused?.(String(payload.message ?? 'Someone else is speaking.'))
          break
        case 'error':
          // The server has refused us. Retrying the same rejection every ten
          // seconds helps nobody; stop and say why.
          this.closedByUs = true
          this.handlers.onRefused?.(String(payload.message ?? 'The session refused this connection.'))
          break
      }
    })

    socket.addEventListener('close', () => {
      window.clearInterval(this.pingTimer)
      this.handlers.onStatus?.('closed')
      if (this.closedByUs) return
      // Back off, but not so far that a listener stands in a dead zone for a
      // minute after signal returns. The handle is kept so close() can cancel
      // a pending attempt — otherwise leaving the page schedules a socket that
      // reopens after everything holding it has gone.
      this.reconnectTimer = window.setTimeout(() => this.connect(), this.reconnectDelayMs)
      this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 10_000)
    })
  }

  close(): void {
    this.closedByUs = true
    window.clearInterval(this.pingTimer)
    window.clearTimeout(this.reconnectTimer)
    this.reconnectTimer = undefined
    this.socket?.close()
  }

  private send(message: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message))
  }

  private ping(): void {
    this.send({ type: 'ping', t: Date.now() })
  }

  sendPose(pose: Pose): void {
    this.send({ type: 'pose', pose })
  }

  sendHealth(health: Record<string, unknown>): void {
    this.send({ type: 'health', health })
  }

  sendBroadcastStart(position: { x: number; y: number } | null): void {
    this.send({ type: 'broadcastStart', position })
  }

  sendBroadcastStop(): void {
    this.send({ type: 'broadcastStop' })
  }

  sendBroadcastPose(position: { x: number; y: number }): void {
    this.send({ type: 'broadcastPose', position })
  }

  sendRtc(to: string, payload: unknown): void {
    this.send({ type: 'rtc', to, payload })
  }

  /** Tells the log about a rule this device decided for itself. */
  sendClientEvent(ruleId: string, triggerType: string, atMs: number): void {
    this.send({ type: 'clientEvent', ruleId, triggerType, atMs })
  }
}
